import {
  Button,
  CreateButton,
  EditButton,
  Icons,
  List,
  notification,
  Popconfirm,
  Space,
  Table,
  TableProps,
} from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { IMeasurementUnit, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { deleteMeasurementUnit, getMeasurementUnits } from 'services/measurement-units';
import { SearchIndex } from 'services/meilisearch';
import MetaData from 'utilities/metadata';

export const MeasurementUnitList: React.FC<IResourceComponentsProps> = () => {
  const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>([]);
  const [totalMeasurementUnits, setTotalMeasurementUnits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const fetchMeasurementUnits = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getMeasurementUnits({
        searchString: searchText,
        start: page,
        limit,
      });
      const measurementUnitsData = data.data;
      setMeasurementUnits(measurementUnitsData);
      setTotalMeasurementUnits(data.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    }
    setIsLoading(false);
  }, [searchText, page, limit]);

  useEffect(() => {
    fetchMeasurementUnits();
  }, [fetchMeasurementUnits]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMeasurementUnit(id);
      fetchMeasurementUnits();
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    }
  };

  const pageHeaders = (
    <Space>
      <CreateButton type="primary" />
    </Space>
  );

  const onTableChange: TableProps<IMeasurementUnit>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchNew
        placeholder="Search by Name"
        size="large"
        className="search"
        index={SearchIndex.MeasurementUnit}
        groups={['name']}
        onSelectOption={onSelectOption}
        onReset={fetchMeasurementUnits}
      />
      <CustomTableWithPagination
        total={totalMeasurementUnits}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={measurementUnits}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          pagination={false}
        >
          <Table.Column<IMeasurementUnit> dataIndex="name" title="Name" />
          <Table.Column<IMeasurementUnit> dataIndex="abbreviation" title="Abbreviation" />
          <Table.Column<IMeasurementUnit> dataIndex="description" title="Description" />
          <Table.Column<IMeasurementUnit>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <EditButton size="small" recordItemId={record.id} hideText />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => handleDelete(record.id)}
                  >
                    <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
